import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import {
  SideDrawerProps,
  SideDrawerRef,
} from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { SuggestedActionsTableData } from '../SuggestedActionsTable/data';
import {
  getArrayLength,
  getDataRoomRoute,
  getSellingPortfolio,
  getUserFullName,
} from 'utils';
import { Link } from 'react-router-dom';
import LinkToIcon from 'icons/LinkTo.icon';
import { AvatarWithInitials } from 'components/AvatarWithInitials';
import SuggestedActionSection from '../SuggestedActionSection';
import FolderIcon from 'icons/Folder.icon';
import DottedSpeechBubbleIcon from 'icons/DottedSpeechBubble.icon';
import ChatIcon from 'icons/Chat.icon';
import { Loader } from 'ncoded-component-library';
import { PendingActionsDetailResponse } from './types';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import SuggestedActionInstance from '../SuggestedActionInstance';
import NotesIcon from 'icons/Notes.icon';
import useDisplayMessage from 'hooks/useDisplayChatMessage';
import DocumentWithPencilIcon from 'icons/DocumentWithPencil.icon';
import QuestionMarkCircleIcon from 'icons/QuestionMarkCircle.icon';
import GroupUsersIcon from 'icons/GroupUsers.icon';
import usePastTime from 'hooks/usePastTime';
import { OPEN_FAQ_DRAWER_PARAM } from 'data/global';

import './SuggestedActionsDrawer.styles.scss';

type SuggestedActionsDrawerProps = {
  className?: string;
  suggestedAction: SuggestedActionsTableData;
} & SideDrawerProps;

const SuggestedActionsDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  SuggestedActionsDrawerProps
> = (props, ref) => {
  const { className, suggestedAction, ...rest } = props;

  const { _id, dealId, dealName, dataRoomId, owner, pendingActions } =
    suggestedAction || {};

  const { firstName, lastName } = owner || {};

  const { hasDataRoomFiles, hasFaqQuestion } = pendingActions || {};

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<PendingActionsDetailResponse>();

  const { messages, dealNotes, leadNotes, questions, waitingRoom } = data || {};

  const messagesCount = getArrayLength(messages);
  const dealNotesCount = getArrayLength(dealNotes);
  const leadNotesCount = getArrayLength(leadNotes);
  const questionsCount = getArrayLength(questions);
  const waitingRoomCount = getArrayLength(waitingRoom);

  const numberOfPendingActions =
    messagesCount +
    dealNotesCount +
    leadNotesCount +
    questionsCount +
    waitingRoomCount;

  const { popServerError } = useContext(PopNotificationsContext);

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const classes = classNames('suggested-actions-drawer', className);

  const { t } = useTranslation();

  const { renderText } = useDisplayMessage();

  const getPastTime = usePastTime();

  useEffect(() => {
    if (!_id) return;

    (async () => {
      try {
        setIsLoading(true);
        setData(null);
        const { data } =
          await api.portfolio.admin.getBossModeDealRoomSuggestedActions(dealId);
        setData(data);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [_id, dealId, popServerError]);

  return (
    <SideDrawer ref={drawerRef} className={classes} {...rest}>
      <header>
        <p className="deal-name">{dealName}</p>
        <p className="number-of-suggested-actions">
          {t('numberOfPendingActions', { count: numberOfPendingActions })}
        </p>
        <div className="owner-info">
          <AvatarWithInitials user={owner} />
          <p>{getUserFullName(firstName, lastName)}</p>
        </div>
      </header>

      <main>
        {isLoading && <Loader />}

        {!!messagesCount && (
          <SuggestedActionSection
            Icon={<ChatIcon className="bigger" />}
            text={t('messagesWaiting', { count: messagesCount })}
            to={getSellingPortfolio(dealId, 'leads')}
            linkText={t('goTo', { page: t('leadsBoardLC') })}
          >
            {messages.map(({ _id, firstName, lastName, createdAt, text }) => (
              <SuggestedActionInstance
                key={_id}
                title={getUserFullName(firstName, lastName)}
                date={createdAt}
              >
                {renderText(text)}
              </SuggestedActionInstance>
            ))}
          </SuggestedActionSection>
        )}

        {!!dealNotesCount && (
          <SuggestedActionSection
            Icon={<NotesIcon className="padding" />}
            text={t('dealNotesUnread', { count: dealNotesCount })}
            to={`${getSellingPortfolio(dealId, 'deal')}?newNote=true`}
            linkText={t('seeDealNotes')}
          >
            {dealNotes.map(
              ({ _id, firstName, lastName, createdAt, content }) => (
                <SuggestedActionInstance
                  key={_id}
                  title={getUserFullName(firstName, lastName)}
                  date={createdAt}
                >
                  {renderText(content)}
                </SuggestedActionInstance>
              ),
            )}
          </SuggestedActionSection>
        )}

        {!!leadNotesCount && (
          <SuggestedActionSection
            Icon={<DocumentWithPencilIcon className="bigger" />}
            text={t('leadNotesUnread', { count: leadNotesCount })}
            to={getSellingPortfolio(dealId, 'leads')}
            linkText={t('goTo', { page: t('leadsBoardLC') })}
          >
            {leadNotes.map(
              ({ _id, firstName, lastName, createdAt, content }) => (
                <SuggestedActionInstance
                  key={_id}
                  title={getUserFullName(firstName, lastName)}
                  date={createdAt}
                >
                  {renderText(content)}
                </SuggestedActionInstance>
              ),
            )}
          </SuggestedActionSection>
        )}

        {!!questionsCount && (
          <SuggestedActionSection
            Icon={<QuestionMarkCircleIcon />}
            text={t('questionsWaiting', { count: questionsCount })}
            to={getDataRoomRoute(dataRoomId, 'questions/questions-and-answers')}
            linkText={t('goTo', { page: 'Q&A' })}
          >
            {questions.map(
              ({ _id, firstName, lastName, createdAt, subject, content }) => (
                <SuggestedActionInstance
                  key={_id}
                  title={getUserFullName(firstName, lastName)}
                  date={createdAt}
                  subject={subject}
                >
                  {renderText(content)}
                </SuggestedActionInstance>
              ),
            )}
          </SuggestedActionSection>
        )}

        {!!waitingRoomCount && (
          <SuggestedActionSection
            Icon={<GroupUsersIcon />}
            text={t('waitingInTheWaitingRoom', { count: waitingRoomCount })}
            to={getDataRoomRoute(dataRoomId, 'waiting-room')}
            linkText={t('goTo', { page: t('waitingRoom') })}
          >
            {waitingRoom.map(({ _id, user, requestedDataRoomAccessAt }) => (
              <div key={_id} className="white-panel waiting-room-member">
                <div>
                  <AvatarWithInitials user={user} />
                  <p>{getUserFullName(user.firstName, user.lastName)}</p>
                </div>
                <span>{getPastTime(requestedDataRoomAccessAt)}</span>
              </div>
            ))}
          </SuggestedActionSection>
        )}

        {!hasDataRoomFiles && (
          <SuggestedActionSection
            Icon={<FolderIcon />}
            text={t('dataRoomIsEmpty')}
            to={getDataRoomRoute(dataRoomId, 'documents')}
            linkText={t('goTo', { page: t('dataRoom') })}
            showEmptyState
            emptyStateText={t('hasDataRoomFilesEmptyState')}
          />
        )}

        {!hasFaqQuestion && (
          <SuggestedActionSection
            Icon={<DottedSpeechBubbleIcon />}
            text={t('noFAQadded')}
            to={`${getDataRoomRoute(
              dataRoomId,
              'questions/frequently-asked-questions',
            )}?${OPEN_FAQ_DRAWER_PARAM}=true`}
            linkText={t('goTo', { page: t('FAQ') })}
            showEmptyState
            emptyStateText={t('hasFaqQuestionEmptyState')}
          />
        )}
      </main>

      <footer>
        <Link
          className="ncoded-button ncoded-button--solid"
          to={getSellingPortfolio(dealId)}
        >
          <LinkToIcon />
          {t('openDeal')}
        </Link>
      </footer>
    </SideDrawer>
  );
};

export default forwardRef(SuggestedActionsDrawer);
